import styles from "./index.module.scss";
import { NextPage } from "next";
import StockOverview from "@/components/stock-overview/StockOverview";
import { Stock } from "@/models/stock";
import Head from "next/head";
import { isUsExchange } from "@/lib/vars";
import { buildStockOgImage } from "@/lib/stock-og";
import { StockJsonLd } from "@/components/json-ld/JsonLd";
import { useSetAtom } from "jotai";
import { stockTradingItemIdState } from "@/lib/store";
import { useEffect } from "react";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { latestNewsExtraProps } from "@/controllers/stock-news.server";
import { stockCanonicalUrl } from "@/lib/stock-url";
import { shortHeadline } from "@/lib/short-key-data";
import { stockTitle } from "@/lib/stock-seo";
import ShortInterestPreloader from "@/components/short-interest-preloader/ShortInterestPreloader";

const StockOverviewPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    // From the stock payload itself (ortex/ortex-gui#1289): no extra request on the render path.
    const shortData = stock?.shortKeyData;
    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);

    return (
        <>
            {stock &&
                (() => {
                    /*
                     * Twitter / iMessage / WhatsApp / Slack / LinkedIn
                     * share preview wiring.
                     *
                     * Before this block existed, every stock URL shared
                     * on social fell back to the global default OG image
                     * — every NVDA, TSLA, AAPL share looked identical.
                     * Now we point `og:image` and `twitter:image` at the
                     * dynamic /api/og endpoint (added in this same MR)
                     * which renders a 1200×630 card with the company
                     * logo (pulled from logo.ortex.com/{sid}r.png), the
                     * ticker pill, and ORTEX branding.
                     *
                     * `twitter:card=summary_large_image` is what tells
                     * Twitter to render the card with the big image at
                     * the top rather than the small thumbnail layout.
                     *
                     * Keeps absolute URLs so the social-fetcher bots
                     * (which don't follow same-origin rewrites) resolve
                     * correctly.
                     */
                    const titleText = stockTitle(stock, "");
                    // Description is the most-cited string by AI answer
                    // engines (Perplexity, ChatGPT) when they reference
                    // a stock — flesh it out beyond the bare minimum so
                    // the cite includes a useful sentence. Mentions
                    // short interest, options flow (where applicable),
                    // analysts, insiders and ORTEX scores by name so
                    // queries containing those terms surface the page.
                    const usListing = isUsExchange(stock.exchangesymbol);
                    // The headline numbers lead, when we have them: they are what a snippet or an AI answer quotes.
                    const description = `${shortHeadline(stock.ticker, shortData)}${shortData ? " " : ""}Live ${stock.name ? `${stock.name} (${stock.exchangesymbol}:${stock.ticker})` : `${stock.exchangesymbol}:${stock.ticker}`} short interest, ${usListing ? "options flow, " : ""}analyst consensus, insider transactions, institutional holdings and ORTEX stock scores. Real-time data from ORTEX: Global Financial Analytics.`;
                    const canonicalUrl = stockCanonicalUrl(stock);
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "Overview", shortData);
                    return (
                        <>
                            <StockJsonLd stock={stock} />
                            <Head>
                                <title>{titleText}</title>
                                <link rel="canonical" href={canonicalUrl} />
                                <meta
                                    name="keywords"
                                    content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, Short Interest, ${
                                        isUsExchange(stock.exchangesymbol) ? "Options, " : ""
                                    }Estimates, Analysts, Insiders, Valuation, EPS, DPS, News, Peers, Events, Short Interests, Shorts, Director Dealings`}
                                />
                                <meta name="description" content={description} />
                                {/* Twitter card */}
                                <meta name="twitter:card" content="summary_large_image" />
                                <meta name="twitter:site" content="@ORTEX" />
                                <meta name="twitter:title" content={titleText} />
                                <meta name="twitter:description" content={description} />
                                <meta name="twitter:url" content={canonicalUrl} />
                                <meta name="twitter:image" content={ogImageUrl} />
                                <meta name="twitter:image:alt" content={cardTitle} />
                                {/* Open Graph (Facebook / LinkedIn / iMessage / Slack / WhatsApp) */}
                                <meta property="og:type" content="website" />
                                <meta property="og:site_name" content="ORTEX" />
                                <meta property="og:title" content={titleText} />
                                <meta property="og:description" content={description} />
                                <meta property="og:url" content={canonicalUrl} />
                                <meta property="og:image" content={ogImageUrl} key="og:image" />
                                <meta property="og:image:width" content="1200" />
                                <meta property="og:image:height" content="630" />
                                <meta property="og:image:alt" content={cardTitle} />
                            </Head>
                        </>
                    );
                })()}
            <div className={styles.StockOverviewPage}>{stock ? <StockOverview stock={stock} /> : <h1>Stock not found.</h1>}</div>
            {/* The most visited tab after this one: its code, props and (signed in) data, once start-up is over (#1350). */}
            {stock && <ShortInterestPreloader stock={stock} />}
        </>
    );
};

export const getServerSideProps = stockPageGetServerSideProps({
    tab: "",
    // RootLayout renders the key-data block (StockKeyData) and the latest ortex.news headlines below the page.
    extraProps: latestNewsExtraProps,
});

export default StockOverviewPage;
